import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { RotateCw } from 'lucide-react';
import { addToCart } from '../api';
import { useToast } from '../hooks/use-toast';

const FALLBACK_IMG =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 350 350%22%3E%3Crect width=%22350%22 height=%22350%22 fill=%22%23111%22/%3E%3Ctext x=%22175%22 y=%22175%22 fill=%22%23ff1f4d%22 text-anchor=%22middle%22 font-family=%22sans-serif%22 font-size=%2218%22 font-weight=%22bold%22%3E2.TURNT%3C/text%3E%3Ctext x=%22175%22 y=%22200%22 fill=%22%23666%22 text-anchor=%22middle%22 font-family=%22sans-serif%22 font-size=%2212%22%3EImage unavailable%3C/text%3E%3C/svg%3E';

export const ProductCard = ({ product }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [showBack, setShowBack] = useState(false); // card image flip
  const [modalShowBack, setModalShowBack] = useState(false); // modal image flip
  const { toast } = useToast();

  const hasBackImage = Boolean(product.back_image);
  const currentImage = showBack && hasBackImage ? product.back_image : product.image;
  const modalImage =
    modalShowBack && hasBackImage ? product.back_image : product.image;

  // "NEW" badge — product created within last 30 days
  const isNew = (() => {
    if (!product.created_at) return false;
    try {
      const created = new Date(product.created_at);
      const daysSince = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
      return daysSince <= 30;
    } catch {
      return false;
    }
  })();

  const isComingSoon = product.inStock === false;

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (hasBackImage) {
      setShowBack(!showBack);
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast({
        title: 'Select a size',
        description: 'Please choose a size before adding to cart',
        variant: 'destructive',
      });
      return;
    }

    addToCart(product, selectedSize);
    window.dispatchEvent(new Event('cartUpdated'));

    toast({
      title: 'Added to cart!',
      description: `${product.name} (${selectedSize}) added to your cart`,
    });

    setIsOpen(false);
    setSelectedSize('');
    setModalShowBack(false);
  };

  return (
    <>
      <Card className="bg-[#111] border border-[#222] overflow-hidden transition-all duration-300 hover:-translate-y-3 hover:shadow-[0_0_30px_rgba(255,31,77,0.3)] group">
        <div
          className={`overflow-hidden bg-[#0a0a0a] relative ${hasBackImage ? 'cursor-pointer' : ''}`}
          onClick={handleImageClick}
          data-testid={`product-image-${product.id}`}
        >
          <img
            src={currentImage}
            alt={product.name}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              // Only show fallback after a genuine failure, not slow loads.
              // Retry once after a short delay before falling back.
              if (!e.target.dataset.retried) {
                e.target.dataset.retried = '1';
                const original = e.target.src;
                e.target.src = '';
                setTimeout(() => { e.target.src = original; }, 500);
                return;
              }
              e.target.onerror = null;
              e.target.src = FALLBACK_IMG;
            }}
            className="w-full h-[350px] object-cover transition-all duration-500 group-hover:scale-110"
          />
          {hasBackImage && (
            <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs text-white border border-white/20">
              <RotateCw className="h-3 w-3" />
              <span>{showBack ? 'Back' : 'Front'}</span>
            </div>
          )}
          {isNew && !isComingSoon && (
            <div className="absolute top-3 left-3 bg-[#ff1f4d] text-white px-3 py-1.5 rounded-full text-xs font-black tracking-widest border border-white/20 shadow-lg" data-testid={`new-badge-${product.id}`}>
              NEW DROP
            </div>
          )}
          {isComingSoon && (
            <div className="absolute top-3 left-3 bg-yellow-500 text-black px-3 py-1.5 rounded-full text-xs font-black tracking-widest border border-white/20 shadow-lg" data-testid={`coming-soon-badge-${product.id}`}>
              COMING SOON
            </div>
          )}
          {hasBackImage && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-[#ff1f4d]/90 px-3 py-1 rounded-full text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Click to flip
            </div>
          )}
        </div>

        <div className="p-5">
          <h3 className="text-white text-xl font-semibold mb-2">{product.name}</h3>
          <p className="text-[#ff1f4d] text-lg font-bold mb-4">${product.price}</p>

          <Button
            onClick={() => setIsOpen(true)}
            disabled={isComingSoon}
            data-testid={`add-to-cart-${product.id}`}
            className="w-full bg-[#ff1f4d] hover:bg-white hover:text-black font-bold transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0 disabled:bg-gray-700 disabled:text-gray-300"
          >
            {isComingSoon ? 'Notify Me' : 'Add To Cart'}
          </Button>
        </div>
      </Card>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) setModalShowBack(false);
        }}
      >
        <DialogContent className="bg-[#111] border-[#222] text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl">{product.name}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {product.description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div
              className={`relative bg-[#0a0a0a] rounded-lg overflow-hidden ${hasBackImage ? 'cursor-pointer' : ''}`}
              onClick={() => hasBackImage && setModalShowBack(!modalShowBack)}
              data-testid={`modal-image-${product.id}`}
            >
              <img
                src={modalImage}
                alt={product.name}
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  if (!e.target.dataset.retried) {
                    e.target.dataset.retried = '1';
                    const original = e.target.src;
                    e.target.src = '';
                    setTimeout(() => { e.target.src = original; }, 500);
                    return;
                  }
                  e.target.onerror = null;
                  e.target.src = FALLBACK_IMG;
                }}
                className="w-full h-64 object-cover"
              />
              {hasBackImage && (
                <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs text-white border border-white/20">
                  <RotateCw className="h-3 w-3" />
                  <span>{modalShowBack ? 'Back' : 'Front'} • Tap to flip</span>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-[#ff1f4d]">${product.price}</span>
              <span className="text-sm text-gray-400">
                {product.inStock ? 'In Stock' : 'Out of Stock'}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Select Size</label>
              <Select value={selectedSize} onValueChange={setSelectedSize}>
                <SelectTrigger
                  className="bg-black border-[#222] text-white"
                  data-testid={`size-select-${product.id}`}
                >
                  <SelectValue placeholder="Choose a size" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#222] text-white">
                  {product.sizes.map((size) => (
                    <SelectItem key={size} value={size} className="hover:bg-[#222]">
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleAddToCart}
              data-testid={`confirm-add-to-cart-${product.id}`}
              className="w-full bg-[#ff1f4d] hover:bg-white hover:text-black font-bold py-6 text-lg"
              disabled={!product.inStock}
            >
              Add To Cart
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
